'use client';

import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import type { ChatInputProps } from './types';
import { useVoiceInput } from '../search/useVoiceInput';
import VoiceMicButton from '../search/VoiceMicButton';

export default function ChatInput({ onSend, disabled, autoFocus }: ChatInputProps & { autoFocus?: boolean }) {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus on mount only from `md` up. On a phone this would pop the software
  // keyboard the instant the chat opens — covering ~40% of the screen and
  // shoving Αριάδνη's greeting («Ρωτήστε με για σκευάσματα, δραστικές ουσίες,
  // κωδικούς ΕΟΦ…») out of view before it has been read. That greeting is the
  // onboarding, so hiding it is the opposite of helpful, and it stacks a second
  // large layout change on top of the panel's slide-in.
  // Done imperatively rather than via the `autoFocus` attribute because the
  // decision depends on `window`, which does not exist during SSR.
  useEffect(() => {
    if (!autoFocus) return;
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 768px)').matches) return;
    textareaRef.current?.focus();
  }, [autoFocus]);

  // ── Voice input ──────────────────────────────────────────────────────────
  // Speech is ADDED to whatever is already typed and lands in the box — it is
  // not sent. The shopper reads it back and presses send, exactly as with
  // typing; Αριάδνη understands natural sentences, so nothing is rewritten
  // (decision a3b47b79). A longer pause than the search bar, because people
  // stop to think mid-sentence.
  const draftRef = useRef('');
  const handleVoiceFinal = useCallback((heard: string) => {
    const draft = draftRef.current;
    setText(draft ? `${draft} ${heard}` : heard);
    if (window.matchMedia('(min-width: 768px)').matches) textareaRef.current?.focus();
  }, []);
  const voice = useVoiceInput({ silenceMs: 2000, onFinal: handleVoiceFinal });
  const handleMicClick = useCallback(() => {
    if (!voice.listening) draftRef.current = text.trim();
    voice.toggle();
  }, [voice, text]);

  const shown = voice.listening
    ? [draftRef.current, voice.text].filter(Boolean).join(' ')
    : text;

  // Grow with the content (typed or spoken), up to five lines.
  // ⚠️ The chat renders TWO ChatInputs (the phone slide-over and the desktop
  // column) and one is always display:none. A hidden textarea measures
  // scrollHeight 0, and pinning that as an inline height would leave the input
  // invisible if the viewport later crosses `md`. So: empty → natural height,
  // unmeasurable → natural height. A layout effect, so spoken text (which is
  // not a keystroke) never paints one frame at the old height.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const h = shown ? el.scrollHeight : 0;
    el.style.height = h > 0 ? `${Math.min(h, 120)}px` : '';
  }, [shown]);

  const handleSend = useCallback(() => {
    if (!text.trim() || disabled || voice.listening) return;
    onSend(text);
    setText('');
  }, [text, disabled, onSend, voice.listening]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  }, []);

  return (
    <div className="flex items-center gap-2.5 px-5 py-4 border-t border-[#E5E7EB] bg-white">
      <div className={`flex items-center flex-1 min-h-[36px] border border-[#C0C4CC] bg-white focus-within:border-brand ${voice.available ? 'pl-4 pr-0.5' : 'px-4'}`}>
        <textarea
          ref={textareaRef}
          value={shown}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          // Clicking back into the box means "I'm done talking".
          onFocus={voice.listening ? voice.stop : undefined}
          readOnly={voice.listening}
          disabled={disabled}
          placeholder={voice.listening ? voice.strings.listening : 'Γράψε το μήνυμά σου...'}
          rows={1}
          // 16px on touch screens: iOS zooms the WHOLE page into any focused
          // field smaller than that (syfak's viewport meta does not prevent
          // it), which slid the modal around and sent the next taps to the
          // wrong controls. Desktop keeps the 13px design.
          className="flex-1 min-w-0 resize-none text-[13px] [@media(pointer:coarse)]:text-[16px] font-gotham leading-[20px] text-text-primary placeholder:text-[#99A1AF] bg-transparent outline-none disabled:opacity-50 custom-scrollbar"
        />
        {voice.available && (
          <VoiceMicButton
            listening={voice.listening}
            message={voice.message}
            onClick={handleMicClick}
            strings={voice.strings}
            disabled={disabled}
            messagePlacement="above"
          />
        )}
      </div>
      <button
        onClick={handleSend}
        disabled={disabled || voice.listening || !text.trim()}
        className="flex-shrink-0 w-[36px] h-[36px] flex items-center justify-center bg-brand text-brand-text disabled:opacity-30 transition-opacity hover:opacity-80 cursor-pointer"
        aria-label="Send message"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
          <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
}
